import { AlertTriangle } from "lucide-react";

export function AIPolicyPage() {
  return (
    <article className="mx-auto max-w-2xl py-8">
      <h1 className="text-3xl font-semibold tracking-tight">Stance on AI</h1>
      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Last updated: April 2026
      </p>

      <div className="mt-8 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        <div>
          <p className="font-semibold text-red-600 dark:text-red-400">
            Art that replicates or impersonates another artist is prohibited.
          </p>
          <p className="mt-1 text-muted-foreground">
            No style laundering. No "in the style of [living artist]" prompts as a sellable
            service. No selling AI output that was produced by imitating a specific person's
            body of work. We'll remove it and ban the account.
          </p>
        </div>
      </div>

      <H>What's prohibited</H>
      <P>
        Listing a commission service that advertises, implies, or actually produces work
        imitating an identifiable artist. This includes "style of @them" prompts, LoRAs or
        fine-tunes trained on a specific artist without their consent, and any offering that
        functionally laundering someone else's visual identity into a sellable product.
      </P>
      <P>
        Posing AI output as handmade. If your service produces images using generative models,
        you have to say so on the service listing. You can't take a commission as "digital
        painting" and deliver raw diffusion output.
      </P>
      <P>
        Impersonating another artist's account, watermark, signature, or name. Picking a handle
        designed to confuse buyers into thinking they're commissioning someone else is
        impersonation. We treat it the same as catfishing.
      </P>

      <H>What's allowed</H>
      <P>
        Using AI tools in your own workflow — reference generation, inpainting your own
        sketches, cleanup, upscaling, brainstorming — is your business. Disclose it if the
        client would reasonably expect a human to have drawn every pixel.
      </P>
      <P>
        Selling work that is honestly labeled as AI-assisted or AI-generated, as long as it
        doesn't imitate a specific living artist and doesn't train on someone else's work
        without consent.
      </P>
      <P>
        Using AI on the client side — writing your brief, generating reference boards, asking
        for iterations in plain English — is fine. We actually want that to work well.
      </P>

      <H>Training on other people's work</H>
      <P>
        Don't train or fine-tune a model on another artist's portfolio or on assets from this
        platform without written consent from the person who made them. Don't use datasets
        you know to be scraped. If you're not sure the dataset is legit, it isn't.
      </P>

      <H>How we enforce this</H>
      <P>
        Reports go to{" "}
        <a className="underline" href="mailto:abuse@fanfix.me">
          abuse@fanfix.me
        </a>
        . We'll look at the service listing, the delivered work, and public samples. Clear
        impersonation gets a same-day takedown and a ban. Borderline cases get a conversation,
        and if you disagree we'd rather you leave than stay and fight it.
      </P>

      <H>Why this stance</H>
      <P>
        Fanfix exists because commissioned art is a real job. That job gets harder every time
        a buyer shows up expecting to pay $15 for a LoRA of a specific person's style. We're
        not anti-tool. We're anti-laundering. The difference matters.
      </P>

      <H>Contact</H>
      <P>
        Reports: <a className="underline" href="mailto:abuse@fanfix.me">abuse@fanfix.me</a>
        {" · "}
        Appeals: <a className="underline" href="mailto:appeals@fanfix.me">appeals@fanfix.me</a>
      </P>
    </article>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-10 mb-2 text-lg font-semibold tracking-tight">{children}</h2>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{children}</p>;
}
